import type { FrontDoors, PipelineTile } from "../api";
import { ragTone } from "../tone";
import { MEASURES, measureChipClass, measureRag, tileNote, tileToneClass, toneDotClass } from "./measure";

/**
 * A front-door lane tile (`s01_topic_sourcing`, `s02_core_needs`,
 * `s03_topic_router`, `g0_core_needs_approval`). It renders grey with
 * "Needs route tagging" whenever the week's `front_doors.available` is
 * false (spec: front doors have no per-tile feed yet — KET-6640), rather
 * than each tile's own `grey_reason`. Same compact tile shape as `StepTile`.
 */
export function FrontDoorTile({
  tile,
  frontDoors,
  onOpen,
  noun = "week",
}: {
  tile: PipelineTile;
  frontDoors: FrontDoors;
  onOpen: (stageKey: string) => void;
  noun?: "week" | "month";
}) {
  if (!frontDoors.available) {
    return (
      <button
        type="button"
        data-nonmutating
        data-tile={tile.stage_key}
        data-tone="neutral"
        onClick={() => onOpen(tile.stage_key)}
        className={`flex min-w-24 flex-col gap-1 rounded-ks-sm border bg-ks-surface px-2 py-1.5 text-left ${tileToneClass("neutral")}`}
      >
        <span className="flex items-center gap-1.5">
          <span className="text-sm font-extrabold leading-none text-ks-purple">{tile.step ?? tile.stage_key}</span>
          <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${toneDotClass("neutral")}`} />
        </span>
        <span className="text-xs font-semibold leading-tight text-ks-ink">{tile.label}</span>
        <span className="text-[10px] leading-tight text-ks-grey-1">Needs route tagging</span>
      </button>
    );
  }

  const tone = ragTone(tile.overall_rag);
  const note = tileNote(tile, noun);
  return (
    <button
      type="button"
      data-nonmutating
      data-tile={tile.stage_key}
      data-tone={tone}
      onClick={() => onOpen(tile.stage_key)}
      className={`flex min-w-24 flex-col gap-1 rounded-ks-sm border bg-ks-surface px-2 py-1.5 text-left ${tileToneClass(tone)}`}
    >
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-extrabold leading-none text-ks-purple">{tile.step ?? tile.stage_key}</span>
        <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${toneDotClass(tone)}`} />
      </span>
      <span className="text-xs font-semibold leading-tight text-ks-ink">{tile.label}</span>
      <span className="flex gap-1" aria-hidden>
        {MEASURES.map((m) => (
          <span
            key={m.key}
            title={m.name}
            className={`flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold ${measureChipClass(measureRag(tile, m.key))}`}
          >
            {m.letter}
          </span>
        ))}
      </span>
      {note && <span className="text-[10px] leading-tight text-ks-grey-1">{note}</span>}
    </button>
  );
}
